<script setup lang="ts">
import { computed } from "vue";
import type { BreadcrumbItem } from "../../../types";
import { NuxtLink } from "#components";

const props = withDefaults( defineProps<{
  items?: BreadcrumbItem[] | null;
  showHomeLabel?: boolean;
}>(), {
  items: () => [],
  showHomeLabel: false,
} );

const firstPage = computed( () => props.items?.[0] );
</script>

<template>
  <nav
    class="breadcrumb"
    aria-label="Breadcrumb">
    <NuxtLink
      v-if="firstPage && firstPage.url"
      :to="firstPage.url"
      class="breadcrumb__arrow">
      <span class="breadcrumb__arrow-text">
        Back to {{ firstPage.name }}
      </span>
    </NuxtLink>

    <ol
      role="list"
      class="breadcrumb__list">
      <li
        v-for="(page, index) in items"
        :key="page.name">
        <div class="breadcrumb__list-content">
          <slot
            v-if="index === 0"
            name="home-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="1em"
              viewBox="0 0 576 512">
              <path
                fill="currentColor"
                d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c0 2.7-.2 5.4-.5 8.1V472c0 22.1-17.9 40-40 40H456c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1H416 392c-22.1 0-40-17.9-40-40V448 384c0-17.7-14.3-32-32-32H256c-17.7 0-32 14.3-32 32v64 24c0 22.1-17.9 40-40 40H160 128.1c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2H104c-22.1 0-40-17.9-40-40V360c0-.9 0-1.9 .1-2.8V287.6H32c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z" />
            </svg>
          </slot>
          <slot
            v-else
            name="separator">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="1em"
              viewBox="0 0 320 512">
              <path
                fill="currentColor"
                d="M301.3 256l-22.6 22.6-160 160L96 461.3 50.7 416l22.6-22.6L210.7 256 73.4 118.6 50.7 96 96 50.7l22.6 22.6 160 160L301.3 256z" />
            </svg>
          </slot>
          <component
            :is="index === (items?.length ?? 0) - 1 ? 'span' : NuxtLink"
            :href="page.url"
            :aria-current="index === (items?.length ?? 0) - 1 ? 'page': null"
            class="breadcrumb__link">
            {{ page.name || '(Unpublished content)' }}
          </component>
        </div>
      </li>
    </ol>
  </nav>
</template>

<style scoped lang="scss">
.breadcrumb {
  display: flex;

  --item-gap: 1rem;
  --arrow-width: 15px;

  &__arrow {
    position: relative;
    margin: 15px 0 15px 15px;
    background: #f6f6f6;
    padding: 7px 10px;
    color: var(--color-primary-500);
    font-weight: 500;
    text-decoration: none;
    font-size: 0.875rem;
    display: none;
    justify-content: center;
    min-height: 34px;

    @media (max-width: 767px) {
      display: inline-flex;
    }

    &::before {
      content: "";
      display: block;
      position: absolute;
      left: calc( var( --arrow-width ) * -1 );
      top: 0;
      bottom: 0;
      width: var( --arrow-width );
      height: 100%;
      background: inherit;
      clip-path: polygon( 100% 0, 100% 100%, 0 50% );
    }
  }

  &__list {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;

    @media (max-width: 767px) {
      display: none;
    }

    & > * + * {
      margin-left: var( --item-gap );
    }

    &-content {
      display: flex;
      align-items: center;
    }

  }

  &__link {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: var( --item-gap );
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    text-decoration: none;
    color: var(--color-gray-600);
    gap: 1rem;

    &:hover {
      color: var(--color-gray-800);
    }

    &--current {
      &:hover {
        cursor: inherit;
        color: var(--color-gray-600);
      }
    }

    &--home {
      margin-left: 0;
    }
  }

  svg {
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: var(--color-gray-600);
  }

}
</style>
